import React, { useState, useEffect, useRef } from "react";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { buildApiUrl } from "../../lib/api";
import { isSmallAccountMode } from "../../lib/smallAccountMode";
import "./CompaniesMarquee.css";

const SYMBOL_BUCKETS = {
    core:           ["AAPL", "MSFT", "NVDA", "TSLA", "AMZN"],
    earnings_large: ["META", "GOOGL", "NFLX", "AMD"],
    earnings_small: ["PLTR", "SOFI", "RIVN", "DKNG"],
};

const CACHE_TTL_MS = 20_000;

let _cache = null;
let _cacheKey = "";
let _cacheTs = 0;

function getStoredBuyingPower() {
    const raw = localStorage.getItem("savvy_buying_power");
    if (raw == null) return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
}

function getSymbols() {
    const bp = getStoredBuyingPower();
    if (bp !== undefined && isSmallAccountMode(bp)) {
        return SYMBOL_BUCKETS.earnings_small;
    }
    return [...SYMBOL_BUCKETS.core, ...SYMBOL_BUCKETS.earnings_large];
}

function TickerItem({ symbol, pctChange, price }) {
    const positive = pctChange > 0;
    const negative = pctChange < 0;
    const colorClass = positive ? "cm-tick--up" : negative ? "cm-tick--down" : "cm-tick--flat";
    const arrow = positive ? "↑" : negative ? "↓" : "";
    const sign = positive ? "+" : "";

    return (
        <span className={`cm-tick ${colorClass}`}>
            <span className="cm-tick-symbol">{symbol}</span>
            {arrow && <span className="cm-tick-arrow">{arrow}</span>}
            <span className="cm-tick-pct">{sign}{pctChange.toFixed(2)}%</span>
        </span>
    );
}

export default function CompaniesMarquee() {
    const [quotes, setQuotes] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const intervalRef = useRef(null);

    const symbols = getSymbols();
    const symbolsKey = symbols.join(",");

    const fetchQuotes = async (syms, key) => {
        const now = Date.now();
        if (_cacheKey === key && _cache && now - _cacheTs < CACHE_TTL_MS) {
            setQuotes(_cache);
            setLoaded(true);
            return;
        }

        try {
            const { data } = await axios.get(
                buildApiUrl("/market/quotes"),
                { params: { symbols: syms.join(",") }, withCredentials: true }
            );
            if (Array.isArray(data) && data.length > 0) {
                _cache = data;
                _cacheKey = key;
                _cacheTs = Date.now();
                setQuotes(data);
            }
        } catch {
            // Non-critical — keep showing stale data or nothing
        } finally {
            setLoaded(true);
        }
    };

    useEffect(() => {
        fetchQuotes(symbols, symbolsKey);
        intervalRef.current = setInterval(() => fetchQuotes(symbols, symbolsKey), CACHE_TTL_MS);
        return () => clearInterval(intervalRef.current);
    }, [symbolsKey]);

    if (!loaded || quotes.length === 0) return null;

    // Duplicate items so the loop always has enough content to look seamless
    const items = quotes.length < 6 ? [...quotes, ...quotes] : quotes;

    return (
        <div className="cm-wrapper">
            <Marquee gradient={false} speed={32} pauseOnHover>
                {items.map((q, i) => (
                    <TickerItem
                        key={`${q.symbol}-${i}`}
                        symbol={q.symbol}
                        pctChange={q.pct_change}
                        price={q.price}
                    />
                ))}
            </Marquee>
        </div>
    );
}
